import React, { useEffect, useMemo, useState } from 'react';

import { Badge, Button, Table } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faExternalLinkAlt, faFileExport, faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import Select from 'react-select';

import AsyncItemCountBadge from 'components/shared/AsyncItemCountBadge';
import AttributeTermsEditModal from 'components/l4_segmentation/AttributeTermsEditModal';
import {
  backgroundGrey,
  blackberry,
  darkGray,
  disabledGray,
  grape,
  greyBorder,
  lightIndigo,
  lightPurple2,
  mutedGray,
  standardGray,
  white,
} from 'lib/css/colors';
import { exportFileErrorSwal, exportFileSuccessSwal } from 'lib/utils/toast';
import {
  L4_CATEGORY_DIVISION_EXPORT_SEGMENTATION_VIEW,
  L4_CATEGORY_DIVISION_PRODUCTS_BY_ATTRIBUTE,
} from 'lib/networking/endpoints';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import PaginationComponent from 'components/shared/PaginationComponent';
import { ProductType, ProductTypeDisplay } from 'lib/enums/ProductType';
import useGet from 'lib/hooks/useGet';
import useL4SegmentationUrlParams from 'lib/hooks/useL4SegmentationUrlParams';
import usePost from 'lib/hooks/usePost';

const DEFAULT_PAGE_SIZE = 50;
const VALUE_FILTER_LABEL = 'Value';

const selectStyles = {
  container: provided => ({ ...provided, minWidth: 220 }),
  control: (provided, state) => ({
    ...provided,
    fontSize: '13px',
    borderColor: state.isFocused ? grape : greyBorder,
    boxShadow: state.isFocused ? `0 0 0 2px ${lightIndigo}` : 'none',
    borderRadius: '6px',
    minHeight: '34px',
    ':hover': { borderColor: grape },
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: '13px',
    backgroundColor: state.isSelected ? blackberry : state.isFocused ? lightPurple2 : white,
    color: state.isSelected ? white : darkGray,
  }),
  singleValue: provided => ({ ...provided, fontSize: '13px', color: darkGray }),
  placeholder: provided => ({ ...provided, fontSize: '13px', color: mutedGray }),
};

function SegmentationValueFilterBar({
  valueOptions,
  selectedValue,
  onValueChange,
  totalCount,
  loading,
}) {
  return (
    <div className={css(styles.filterToolbar)}>
      <div className={css(styles.valueFilterInline)}>
        <span className={css(styles.filterLabel)}>{VALUE_FILTER_LABEL}</span>
        <Select
          options={valueOptions}
          value={selectedValue}
          onChange={onValueChange}
          placeholder="All"
          isClearable
          styles={selectStyles}
        />
      </div>
      <AsyncItemCountBadge loading={loading} count={totalCount} />
    </div>
  );
}

SegmentationValueFilterBar.propTypes = {
  valueOptions: PropTypes.array.isRequired,
  selectedValue: PropTypes.object,
  onValueChange: PropTypes.func.isRequired,
  totalCount: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
};

function AttributeGroupView({ attributeType, attributeData, category }) {
  const { params, replaceParams } = useL4SegmentationUrlParams();
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(() => params.page);
  const [itemsPerPage, setItemsPerPage] = useState(() => params.perPage || DEFAULT_PAGE_SIZE);
  const [selectedValueOption, setSelectedValueOption] = useState(() =>
    params.value ? { label: params.value, value: params.value } : null,
  );

  const { postData: postExportSegmentationView, loading: exportingSegmentationView } = usePost(
    L4_CATEGORY_DIVISION_EXPORT_SEGMENTATION_VIEW,
    () =>
      exportFileSuccessSwal(
        'The segmentation table export has been queued. The CSV file will be generated in a while.',
      ),
    err =>
      exportFileErrorSwal(
        err?.response?.data?.error || 'An error occurred while trying to export the file',
      ),
  );

  useEffect(() => {
    setCurrentPage(params.page);
    if (params.perPage) setItemsPerPage(params.perPage);
  }, [params.page, params.perPage]);

  // valueOptions below is useMemo'd for the Select; this effect only maps URL ?value= to a
  // react-select option (including ad-hoc labels). Memoizing opts here would not reduce work
  // meaningfully because the effect runs only when attributeData or params.value changes.

  useEffect(() => {
    if (!params.value) {
      setSelectedValueOption(null);
      return;
    }
    const opts = (attributeData?.values || []).map(v => ({ label: v.name, value: v.name }));
    const match = opts.find(o => o.value === params.value);
    setSelectedValueOption(match || { label: params.value, value: params.value });
  }, [attributeData, params.value]);

  const offset = (currentPage - 1) * itemsPerPage;

  const valueOptions = useMemo(
    () =>
      (attributeData?.values || []).map(v => ({
        label: v.name,
        value: v.name,
      })),
    [attributeData],
  );

  const {
    data: { data: products = [], total_count: totalCount = 0 } = {},
    loading,
    error,
  } = useGet(L4_CATEGORY_DIVISION_PRODUCTS_BY_ATTRIBUTE, {
    l4_category_id: category.id,
    attribute_type: attributeType,
    limit: itemsPerPage,
    offset,
    ...(selectedValueOption ? { attribute_value: selectedValueOption.value } : {}),
  });

  const handlePageSizeChange = newSize => {
    setItemsPerPage(newSize);
    setCurrentPage(1);
    replaceParams({
      perPage: newSize === DEFAULT_PAGE_SIZE ? null : newSize,
      page: null,
    });
  };

  const handlePageChange = page => {
    setCurrentPage(page);
    replaceParams({ page: page <= 1 ? null : page });
  };

  const handleValueFilterChange = option => {
    setSelectedValueOption(option);
    setCurrentPage(1);
    replaceParams({ value: option?.value ?? null, page: null });
  };

  if (!attributeData) {
    return <div className={css(styles.emptyState)}>No data available for this attribute.</div>;
  }

  const { values = [] } = attributeData;

  const displayProductName = product =>
    product.product_name || product.name_deabbreviated || product.name_clean || null;

  const classNameForRow = product => {
    const valuePart = product.segmentation_attribute_value;
    if (!valuePart) {
      return '—';
    }
    return `${category.category_name} - ${valuePart}`;
  };

  const handleExportTable = () => {
    postExportSegmentationView({
      l4_category_id: category.id,
      l4_category_name: category.category_name,
      attribute_type: attributeType,
      ...(selectedValueOption ? { attribute_value: selectedValueOption.value } : {}),
    });
  };

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.titleRow)}>
        <div className={css(styles.titleLeft)}>
          <h5 className="mb-0 font-weight-bold">{attributeType}</h5>
        </div>
        <div className={css(styles.titleActions)}>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleExportTable}
            disabled={exportingSegmentationView}
            className={css(styles.exportBtn)}
            aria-label="Export current table to CSV"
          >
            {exportingSegmentationView ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            ) : (
              <>
                <FontAwesomeIcon icon={faFileExport} className="mr-1" />
                Export table
              </>
            )}
          </Button>
          <Button
            size="sm"
            onClick={() => setShowEditModal(true)}
            className={css(styles.editBtn)}
            aria-label={`Edit terms for ${attributeType}`}
          >
            <FontAwesomeIcon icon={faPencilAlt} className="mr-1" />
            Edit Terms &amp; Re-segment
          </Button>
        </div>
      </div>

      <SegmentationValueFilterBar
        valueOptions={valueOptions}
        selectedValue={selectedValueOption}
        onValueChange={handleValueFilterChange}
        totalCount={totalCount}
        loading={loading}
      />

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <div className={css(styles.errorState)}>
          Failed to load products. Please try again later.
        </div>
      ) : products.length === 0 ? (
        <div className={css(styles.emptyState)}>
          No products found for this attribute
          {selectedValueOption ? ` (${selectedValueOption.value}).` : '.'}
        </div>
      ) : (
        <>
          <div className={css(styles.tableWrapper)}>
            <Table hover responsive className={css(styles.table)}>
              <thead className={css(styles.tableHead)}>
                <tr>
                  <th className={css(styles.tableHeadCell)} style={{ width: '25%' }}>
                    Product Name
                  </th>
                  <th className={css(styles.tableHeadCell)} style={{ width: '14%' }}>
                    Brand
                  </th>
                  <th className={css(styles.tableHeadCell)} style={{ width: '18%' }}>
                    Mfr / Vendor
                  </th>
                  <th className={css(styles.tableHeadCell)} style={{ width: '10%' }}>
                    Type
                  </th>
                  <th className={css(styles.tableHeadCell)} style={{ width: '18%' }}>
                    {VALUE_FILTER_LABEL}
                  </th>
                  <th className={css(styles.tableHeadCell)} style={{ width: '10%' }}>
                    Class Name
                  </th>
                  <th className={css(styles.tableHeadCell)} style={{ width: '5%' }}>
                    View
                  </th>
                </tr>
              </thead>
              <tbody>
                {products.map((product, index) => (
                  <tr key={product.id || index}>
                    <td>
                      <span className={css(styles.cellText)}>
                        {displayProductName(product) || <span className={css(styles.na)}>N/A</span>}
                      </span>
                    </td>
                    <td>
                      <span className={css(styles.brandName)}>
                        {product.brand_name || <span className={css(styles.na)}>Unknown</span>}
                      </span>
                    </td>
                    <td>
                      <span className={css(styles.cellText)}>
                        {product.entity_name || <span className={css(styles.na)}>N/A</span>}
                      </span>
                    </td>
                    <td className="text-center">
                      <Badge
                        variant={
                          product.product_type === ProductType.MANUFACTURER ? 'info' : 'secondary'
                        }
                      >
                        {product.product_type === ProductType.MANUFACTURER
                          ? ProductTypeDisplay.MANUFACTURER
                          : ProductTypeDisplay.DISTRIBUTOR}
                      </Badge>
                    </td>
                    <td>
                      <span className={css(styles.valueCell)}>
                        {product.segmentation_attribute_value || (
                          <span className={css(styles.na)}>—</span>
                        )}
                      </span>
                    </td>
                    <td>
                      <span className={css(styles.cellText)}>{classNameForRow(product)}</span>
                    </td>
                    <td className="text-center">
                      {product.global_product_id && product.product_id && product.product_type ? (
                        <a
                          href={`/global-product-detail/${product.global_product_id}/${product.product_id}/${product.product_type}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={css(styles.productLink)}
                          aria-label="View product"
                        >
                          <FontAwesomeIcon icon={faExternalLinkAlt} />
                        </a>
                      ) : (
                        <span className={css(styles.na)}>-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <div className={css(styles.paginationWrapper)}>
            <PaginationComponent
              totalItems={totalCount}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={handlePageSizeChange}
              currentPage={currentPage}
              setCurrentPage={handlePageChange}
              disabled={loading}
              defaultSize
            />
          </div>
        </>
      )}

      {showEditModal && (
        <AttributeTermsEditModal
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          attributeType={attributeType}
          category={category}
          initialValues={values}
        />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px',
    paddingBottom: '12px',
    borderBottom: `1px solid ${greyBorder}`,
    flexShrink: 0,
  },
  titleLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0,
    flexShrink: 1,
  },
  titleActions: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '8px',
    justifyContent: 'flex-end',
    flexShrink: 0,
  },
  exportBtn: {
    fontWeight: 500,
  },
  editBtn: {
    backgroundColor: grape,
    borderColor: grape,
    color: white,
    fontSize: '13px',
    fontWeight: 500,
  },
  filterToolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: '12px',
    padding: '10px 0',
    marginBottom: '8px',
    borderBottom: `1px solid ${greyBorder}`,
    flexShrink: 0,
  },
  valueFilterInline: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    zIndex: 999,
  },
  filterLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: darkGray,
    whiteSpace: 'nowrap',
  },
  tableWrapper: {
    flex: 1,
    overflowY: 'auto',
    marginBottom: '16px',
    minHeight: 0,
  },
  table: {
    marginBottom: 0,
  },
  tableHead: {
    backgroundColor: backgroundGrey,
  },
  tableHeadCell: {
    position: 'sticky',
    top: 0,
    backgroundColor: backgroundGrey,
    zIndex: 2,
    boxShadow: `0 1px 0 ${greyBorder}`,
  },
  cellText: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '220px',
    fontSize: '13px',
  },
  brandName: {
    fontSize: '13px',
    fontWeight: 600,
    color: darkGray,
  },
  valueCell: {
    fontSize: '13px',
    fontWeight: 500,
    color: grape,
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '180px',
  },
  na: {
    color: disabledGray,
    fontStyle: 'italic',
  },
  productLink: {
    color: blackberry,
    fontSize: '14px',
    ':hover': {
      color: grape,
    },
  },
  paginationWrapper: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '16px',
    borderTop: `1px solid ${greyBorder}`,
    flexShrink: 0,
  },
  emptyState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '200px',
    color: standardGray,
    fontSize: '14px',
    textAlign: 'center',
    padding: '16px',
  },
  errorState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '200px',
    color: standardGray,
    fontSize: '14px',
  },
});

AttributeGroupView.propTypes = {
  attributeType: PropTypes.string.isRequired,
  attributeData: PropTypes.shape({
    attribute_type: PropTypes.string,
    total_count: PropTypes.number,
    values: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        count: PropTypes.number,
      }),
    ),
  }),
  category: PropTypes.shape({
    id: PropTypes.number.isRequired,
    category_name: PropTypes.string.isRequired,
  }).isRequired,
};

export default AttributeGroupView;
